import { action, computed, makeObservable, observable } from 'mobx';

import { getRegisteredDomain, urlToDomain } from '../lib/utils/domains';
import { StatisticsNodeForRequestsForRegisteredDomain, UUID, StatisticsForRequestsForUrl, StatisticsNodeForRequestsForDomainNode, NetworkOriginStatisticsTree, RecordingNetworkStatisticsTree, DbRecordedEvent, RecordingDataSummary, isISODateTimeString, nowAsISODateTimeString, StatisticsForNetworkRequests } from './EvidenceDB/DatabaseTypes';
import { mapGetOrCreate, toMap } from '../lib/utils/mapGetOrCreate';
import { isEventNetworkEvent, ISODateTimeString } from '../lib/Events';

export type OriginSortType = 'alphanumeric' | 'chronological';
export type SortDirection = 'ascending' | 'descending';

interface SourcePageAndDestinationPageUrls {
	sourcePageUrl: string;
	destinationPageUrl: string;
}
interface SourcePageAndDestinationPageUrlsWithMetadata extends SourcePageAndDestinationPageUrls, StatisticsForNetworkRequests {}
const sourcePageAndDestinationPageUrlsKey = ({sourcePageUrl, destinationPageUrl}: SourcePageAndDestinationPageUrls) =>
	`${sourcePageUrl}\0${destinationPageUrl}`;


export const updateOriginNetworkStatisticsTree = action( (
	root: NetworkOriginStatisticsTree
) => (
	...urlCountPairs: ({url: string} & StatisticsForNetworkRequests)[]
): NetworkOriginStatisticsTree => {
	for (const {url, numberOfRequests, whenFirstObservedIso, whenLastObservedIso} of urlCountPairs) {
		const domain = urlToDomain(url);
		const registeredDomain = getRegisteredDomain(domain);
		if (domain == null || registeredDomain == null) continue;
		const registeredDomainObserved = mapGetOrCreate(root, registeredDomain, () => ({
			registeredDomain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests: 0, domains: observable.map<string, StatisticsNodeForRequestsForDomainNode>()
		} satisfies StatisticsNodeForRequestsForRegisteredDomain as StatisticsNodeForRequestsForRegisteredDomain));
		registeredDomainObserved.numberOfRequests += numberOfRequests;
		if (whenFirstObservedIso < registeredDomainObserved.whenFirstObservedIso) {
			registeredDomainObserved.whenFirstObservedIso = whenFirstObservedIso;
		}
		if (whenLastObservedIso > registeredDomainObserved.whenLastObservedIso) {
			registeredDomainObserved.whenLastObservedIso = whenLastObservedIso;
		}

		const domainObserved = mapGetOrCreate(registeredDomainObserved.domains, domain, () => ({
			domain, whenFirstObservedIso, whenLastObservedIso, numberOfRequests: 0, urls: observable.map<string, StatisticsForRequestsForUrl>()
		} satisfies StatisticsNodeForRequestsForDomainNode as StatisticsNodeForRequestsForDomainNode));
		domainObserved.numberOfRequests += numberOfRequests;
		if (whenFirstObservedIso < domainObserved.whenFirstObservedIso) {
			domainObserved.whenFirstObservedIso = whenFirstObservedIso;
		}
		if (whenLastObservedIso > domainObserved.whenLastObservedIso) {
			domainObserved.whenLastObservedIso = whenLastObservedIso;
		}

		const urlObserved = mapGetOrCreate(domainObserved.urls, url, () => ({
			url, whenFirstObservedIso, whenLastObservedIso, numberOfRequests
		} satisfies StatisticsForRequestsForUrl as StatisticsForRequestsForUrl));
		if (whenFirstObservedIso < urlObserved.whenFirstObservedIso) {
			urlObserved.whenFirstObservedIso = whenFirstObservedIso;
		}
		if (whenLastObservedIso > urlObserved.whenLastObservedIso) {
			urlObserved.whenLastObservedIso = whenLastObservedIso;
		}
	}
	return root;
} )

export const createOriginNetworkStatisticsTree = (urlCountPairs: ({url: string} & StatisticsForNetworkRequests)[]): NetworkOriginStatisticsTree => {
	return updateOriginNetworkStatisticsTree(observable.map<string, StatisticsNodeForRequestsForRegisteredDomain>())(...urlCountPairs);
}


export class NetworkTrafficRecordingState implements RecordingNetworkStatisticsTree {
	_whenFirstObservedIso: ISODateTimeString | undefined;
	_whenLastObservedIso: ISODateTimeString | undefined;
	_numberOfEvents: number = 0;
	_numberOfRequests: number = 0;
	_sourcePageAndDestinationPageUrls = observable.map<string, SourcePageAndDestinationPageUrlsWithMetadata>();
	_sourcePageOriginStatisticsTree = observable.map<string, StatisticsNodeForRequestsForRegisteredDomain>();
	_destinationOriginStatisticsTree = observable.map<string, StatisticsNodeForRequestsForRegisteredDomain>();

	constructor(public readonly recordingUuid: UUID) {
		makeObservable(this, {
			_numberOfEvents: observable,
			_numberOfRequests: observable,
			// _sourcePageAndDestinationPageUrls: observable,
			// _sourcePageOriginStatisticsTree: observable,
			// _destinationOriginStatisticsTree: observable,
			_whenFirstObservedIso: observable,
			_whenLastObservedIso: observable,
		});
	}

	@computed get numberOfEvents() { return this._numberOfEvents; }
	@computed get numberOfRequests() { return this._numberOfRequests; }
	@computed get sourcePageAndDestinationPageUrls() { return this._sourcePageAndDestinationPageUrls; }
	@computed get sourcePageOriginStatisticsTree(): NetworkOriginStatisticsTree { return this._sourcePageOriginStatisticsTree; }
	@computed get destinationOriginStatisticsTree(): NetworkOriginStatisticsTree { return this._destinationOriginStatisticsTree; }
	@computed get whenFirstObservedIso() { return isISODateTimeString(this._whenFirstObservedIso) ? this._whenFirstObservedIso : nowAsISODateTimeString() };
	@computed get whenLastObservedIso() { return isISODateTimeString(this._whenLastObservedIso) ? this._whenLastObservedIso : nowAsISODateTimeString() };

	getRecordingDataSummary = (): RecordingDataSummary => {
		const {
			numberOfEvents,
			numberOfRequests,
			whenFirstObservedIso,
			whenLastObservedIso,
			sourcePageOriginStatisticsTree: sourcePageRegisteredDomainsTree,
			destinationOriginStatisticsTree: destinationRegisteredDomainsTree,
		} = this;
		const sourcePageRegisteredDomains = [...sourcePageRegisteredDomainsTree.values()].map( ({domains, ...rest}) => rest  );
		const destinationRegisteredDomains = [...destinationRegisteredDomainsTree.values()].map( ({domains, ...rest}) => rest  );
		return {
			numberOfEvents,
			numberOfRequests,
			whenFirstObservedIso,
			whenLastObservedIso,
			sourcePageRegisteredDomains,
			destinationRegisteredDomains,
		};
	}

	destinationNetworkOriginStatisticsTreeFilteredBySourcePage = (
		sourceUrlFilter: (url: string) => boolean,
		thirdPartiesOnly: boolean
	): NetworkOriginStatisticsTree => {
		let filteredPairs = [...this.sourcePageAndDestinationPageUrls.values()];
		// apply the source URL filter
		filteredPairs = filteredPairs.filter( ({sourcePageUrl}) => sourceUrlFilter(sourcePageUrl) );
		if (thirdPartiesOnly) {
			// apply the third party filter
			filteredPairs = filteredPairs.filter( ({sourcePageUrl, destinationPageUrl}) =>
				getRegisteredDomain(sourcePageUrl) !== getRegisteredDomain(destinationPageUrl)
			);
		}
		const filteredUrls = filteredPairs.map( ({sourcePageUrl, destinationPageUrl, ...metadata}) => ({
			...metadata,
			url: destinationPageUrl
		}));
		return createOriginNetworkStatisticsTree(filteredUrls);
	}

	@computed get sourceRegisteredDomainToThirdPartyDestinations() {
		return [...toMap(
			[...this.sourcePageAndDestinationPageUrls.values()],
			({sourcePageUrl}) => getRegisteredDomain(sourcePageUrl))
			.entries()]
			.map( ([registeredDomain, pairs]) => [
				registeredDomain,
				// remove first party domains
				pairs.filter( (pair) => getRegisteredDomain(pair.destinationPageUrl) !== registeredDomain )
			] as const )
			.filter( (pair): pair is [string, SourcePageAndDestinationPageUrlsWithMetadata[]] =>
				pair[0] != null && pair[1].length > 0
			)
			.map( ([sourceRd, urlParis]) => ([
				sourceRd,
				createOriginNetworkStatisticsTree(urlParis.map( ({sourcePageUrl, destinationPageUrl, ...metadata}) => ({
					...metadata,
					url: destinationPageUrl
				}))
		)] as const))
	}
	

	highestEventIndexProcessed: number = -1;
	requestIds: Set<string> = new Set();

	updateForEvent = action ( (event: DbRecordedEvent): undefined => {
		this._numberOfEvents++;
		const {whenRecordedIso, tab, eventIndex} = event;
		if (eventIndex > this.highestEventIndexProcessed) {
			this.highestEventIndexProcessed = eventIndex;
		}
		if (!tab.url.startsWith('http')) {
			return;
		}
		if (this._whenFirstObservedIso == null || this._whenFirstObservedIso > whenRecordedIso) {
			this._whenFirstObservedIso = whenRecordedIso;
		}
		if (this.whenLastObservedIso == null || this.whenLastObservedIso < whenRecordedIso) {
			this._whenLastObservedIso = whenRecordedIso;
		}
		if (isEventNetworkEvent(event)) {
			const { destination } = event;
			if ( ! ( destination.url.startsWith('http') || destination.url.startsWith('ws') ) ) {
				return;
			}
			// Update count of requests
			this.requestIds.add(event.raw.requestId);
			this._numberOfRequests = this.requestIds.size;

			const sourcePageAndDestinationPageUrls = {sourcePageUrl: tab.url, destinationPageUrl: destination.url};
			const key = sourcePageAndDestinationPageUrlsKey(sourcePageAndDestinationPageUrls);
			const entry = mapGetOrCreate(this._sourcePageAndDestinationPageUrls, key, () => (observable({
				...sourcePageAndDestinationPageUrls,
				numberOfRequests: 0,
				whenFirstObservedIso: whenRecordedIso,
				whenLastObservedIso: whenRecordedIso,
			})));
			entry.numberOfRequests++;
			if (whenRecordedIso < entry.whenFirstObservedIso) {
				entry.whenFirstObservedIso = whenRecordedIso;
			}
			if (whenRecordedIso > entry.whenLastObservedIso) {
				entry.whenLastObservedIso = whenRecordedIso;
			}

			const metaData = {
				url: destination.url,
				whenFirstObservedIso: whenRecordedIso,
				whenLastObservedIso: whenRecordedIso,
				numberOfRequests: 1
			} as const;
			updateOriginNetworkStatisticsTree(this._sourcePageOriginStatisticsTree)({...metaData, url: tab.url});
			updateOriginNetworkStatisticsTree(this._destinationOriginStatisticsTree)({...metaData, url: destination.url});
		}
	});
}
